/*
  LandingPage — AB8 Beauty & Skin storefront landing.
  ───────────────────────────────────────────────────────────────────────────
  Ported from ab8-beauty/home.html. The inline <style> now lives (scoped) in
  LandingPage.module.css; the inline <script> behaviours are React state/effects.
  Shared chrome (<Navbar/>, <Footer/>) replaces the raw nav/footer markup so
  cart/auth/login state stays shared across the app. The 4 shop cards link to
  their product pages via react-router.
*/
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import api from '../utils/api';
import AmbassadorApplyTile from '../components/AmbassadorApplyTile';
import ScrollProgress from '../components/storefront/ScrollProgress';
import CategoryCarousels from '../components/storefront/CategoryCarousels';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FutureHero from '../components/storefront/FutureHero';
import styles from './LandingPage.module.css';

// ── Shop grid: 4 products → product pages ──────────────────────────────────
const PRODUCTS = [
  {
    slug: 'bubble', cat: 'Bubblé', name: 'Pink Collagen Balm Stick',
    desc: 'Volumizing, wrinkle-smoothing multi balm for face & lips.',
    price: '$28', badge: 'Bestseller', thumb: styles.thPink, visual: 'stick',
  },
  {
    slug: 'glace', cat: 'GLACÉ', name: 'Oversized Cryo Eye Patches',
    desc: 'Freezer-to-face cold therapy that de-puffs in 10 minutes.',
    price: 'from $24', thumb: styles.thIce, visual: 'patch',
  },
  {
    slug: 'blue-creme', cat: 'Blue Crème', name: 'GHK-Cu Copper Peptide Cream',
    desc: 'Cooling copper-peptide repair cream for firmer skin.',
    price: '$64', thumb: styles.thBlue, visual: 'jar',
  },
  {
    slug: 'verde', cat: 'VERDÉ', name: 'Rosemary Mint Scalp Therapy',
    desc: 'Cooling rosemary & mint treatment for invigorated roots.',
    price: '$32', thumb: styles.thGreen, visual: 'bottle',
  },
];

// ── Reviews (ported from the home.html script) ─────────────────────────────
const REVIEWS = [
  { n: 'Maya R.',   t: 'Pink Collagen Stick', c: '#ff7fb3', s: 5, verified: true, q: 'Obsessed. My lips and cheeks look plumper within minutes and it never feels greasy. Already on my third one.' },
  { n: 'Jordan L.', t: 'GLACÉ Cryo Patches',  c: '#7fbdf2', s: 5, verified: true, q: 'The cold de-puff is unreal on a Monday morning. I keep a pair in the freezer at all times now.' },
  { n: 'Priya S.',  t: 'Blue Crème GHK-Cu',   c: '#5fa3e0', s: 5, verified: true, q: 'Six weeks in and my skin genuinely looks firmer. The cooling feel makes my whole routine better.' },
  { n: 'Daniel K.', t: 'VERDÉ Scalp Therapy', c: '#5fb87a', s: 5, verified: true, q: 'That rosemary-mint tingle wakes up my whole scalp. Hair feels thicker and so much fresher.' },
  { n: 'Sofia M.',  t: 'Pink Collagen Stick', c: '#ff7fb3', s: 5, verified: true, q: 'The one product I bring everywhere. Lips, cheeks, dry patches — it does it all and looks gorgeous.' },
  { n: 'Elena V.',  t: 'The whole set',       c: '#e85b97', s: 5, verified: true, q: 'Bought the whole set on a whim and now my entire shelf is AB8. Cool, clean, and they actually work.' },
];

const STAR = (s) => '★'.repeat(s) + '☆'.repeat(5 - s);

// ── Per-product tile theming for the "Top products" section ────────────────
const TILE_THEME = {
  bubble:       { grad: 'linear-gradient(160deg,#ffe3ef 0%,#ffbcd9 100%)', ink: '#9d1d54' },
  glace:        { grad: 'linear-gradient(160deg,#ecf6ff 0%,#c5e2fb 100%)', ink: '#1b4a78' },
  'blue-creme': { grad: 'linear-gradient(160deg,#e9f1ff 0%,#c9ddfb 100%)', ink: '#143f73' },
  verde:        { grad: 'linear-gradient(160deg,#e9f8ef 0%,#c7ecd5 100%)', ink: '#1c5e35' },
};

// ── CSS-rendered product visuals for the shop thumbs ───────────────────────
function ThumbVisual({ kind }) {
  switch (kind) {
    case 'stick':
      return (
        <div className={styles.miniStick}>
          <div className={styles.cap} />
          <div className={styles.dome} />
        </div>
      );
    case 'patch':
      return <div className={styles.miniPatch} />;
    case 'jar':
      return <div className={styles.miniJar} />;
    case 'bottle':
      return (
        <div className={styles.miniBottle}>
          <div className={styles.cap} />
        </div>
      );
    default:
      return null;
  }
}

// ── The pink balm stick (CSS render), reused in hero + feature spotlight ────
function BalmStick({ extra = '' }) {
  return (
    <div className={`${styles.stick} ${extra}`}>
      <div className={styles.cap} />
      <div className={styles.dome} />
      <div className={styles.lbl}>
        <b>PDRN Pink Collagen</b><br />
        Volume Multi Balm<br />
        10G / 0.35 OZ
      </div>
      <div className={styles.pct}>5%</div>
    </div>
  );
}

export default function LandingPage() {
  // Pull each product's admin-managed photo from the DB (keyed by slug). The
  // homepage cards/tiles below use it when present, so uploading a product image
  // in the admin updates the homepage. Falls back to the CSS art if none is set.
  const [photos, setPhotos] = useState({});
  useEffect(() => {
    let alive = true;
    api.get('/products')
      .then((res) => {
        const list = res.data.products || res.data.data || (Array.isArray(res.data) ? res.data : []);
        const map = {};
        list.forEach((p) => { if (p.slug && p.imageUrl) map[p.slug] = p.imageUrl; });
        if (alive) setPhotos(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const subscribe = (e) => {
    e.preventDefault();
    e.currentTarget.reset();
    toast.success('Welcome to the glow list — check your inbox for 10% off.');
  };

  return (
    <div className={styles.page}>
      <ScrollProgress color="var(--pink-deep)" height={3} />
      <Navbar />

      {/* ===== HERO ===== */}
      <section className={styles.hero} id="top">
        <div className={styles.heroCard}>
          <div className={styles.heroGlow} />
          <div className={styles.heroLabel}>
            <div className={styles.mono}>Bestseller</div>
            <h4>PDRN Pink Collagen</h4>
          </div>
          <div className={styles.sticks}>
            <BalmStick extra={styles.one} />
            <BalmStick extra={styles.two} />
          </div>
          <div className={styles.heroContent}>
            <span className={styles.pill}>Pink Collagen · Volume · Hydration</span>
            <h1>Skincare,<br />bottled beautifully.</h1>
            <p>
              Meet AB8 — a little family of cult-favorite skin rituals, led by our
              Pink Collagen Volume Multi Balm Stick. Plump, glow, repeat.
            </p>
            <div className={styles.heroCta}>
              <Link className={styles.btnW} to="/shop/bubble">Shop the Pink Stick</Link>
              <a className={styles.btnO} href="#shop">Explore all products</a>
            </div>
          </div>
          <div className={styles.heroIcons}><span>↗</span><span>♡</span></div>
        </div>
      </section>

      {/* ===== TRUST ===== */}
      <div className={styles.trust}>
        <div><span className={styles.star}>★★★★★</span> &nbsp;<b>4.9</b>/5 from 3,200+ reviews</div>
        <div><b>Clean</b> formulas · vegan &amp; cruelty-free</div>
        <div><b>Free</b> shipping over $50</div>
      </div>

      {/* ===== AMBASSADOR APPLY (logged-in only; self-hides otherwise) ===== */}
      <div className={styles.wrap} style={{ marginTop: '2.5rem', marginBottom: '0.5rem' }}>
        <AmbassadorApplyTile />
      </div>

      {/* ===== SHOP GRID ===== */}
      <section className={`${styles.sec} ${styles.wrap}`} id="shop">
        <div className={styles.secHead}>
          <div>
            <div className={styles.eyebrow}>The collection</div>
            <h2>Shop all products</h2>
          </div>
          <Link to="/shop/bubble">View the bestseller →</Link>
        </div>
        <div className={styles.shopGrid}>
          {PRODUCTS.map((p) => (
            <Link className={styles.pcard} to={`/shop/${p.slug}`} key={p.slug}>
              <div className={`${styles.thumb} ${p.thumb}`}>
                {photos[p.slug]
                  ? <img src={photos[p.slug]} alt={p.name}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <ThumbVisual kind={p.visual} />}
                {p.badge && <span className={styles.badge}>{p.badge}</span>}
              </div>
              <div className={styles.meta}>
                <div className={styles.cat}>{p.cat}</div>
                <h3>{p.name}</h3>
                <div className={styles.desc}>{p.desc}</div>
                <div className={styles.row}>
                  <span className={styles.price}>{p.price}</span>
                  <span className={styles.go}>Shop</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SHOP BY CATEGORY (carousel wheels) ===== */}
      <section className={`${styles.sec} ${styles.wrap}`} id="shop-by-category">
        <div className={styles.secHead}>
          <div>
            <div className={styles.eyebrow}>Shop by category</div>
            <h2>Find your ritual</h2>
          </div>
          <a href="#shop">Browse everything →</a>
        </div>
      </section>
      <CategoryCarousels />

      {/* ===== FEATURE SPOTLIGHT ===== */}
      <section className={`${styles.sec} ${styles.wrap}`}>
        <div className={styles.feature}>
          <div className={styles.ftxt}>
            <div className={styles.eyebrow}>Our hero product</div>
            <h2>The Pink Collagen Volume Multi Balm Stick</h2>
            <p>
              One blushing stick that volumizes, smooths fine lines, and floods skin
              with collagen-rich hydration. Swipe it anywhere you want a dewy,
              pillow-soft finish.
            </p>
            <ul>
              <li><span className={styles.ck}>✓</span> Volumizing marine collagen + 5% active complex</li>
              <li><span className={styles.ck}>✓</span> Plumps lips, cheeks, lids &amp; dry spots</li>
              <li><span className={styles.ck}>✓</span> 24-hour cushiony, non-greasy hydration</li>
              <li><span className={styles.ck}>✓</span> Clean, vegan, no parabens or sulfates</li>
            </ul>
            <Link
              className={styles.btnW}
              style={{ background: 'var(--pink-deep)', color: '#fff', boxShadow: '0 12px 30px rgba(232,91,151,.4)' }}
              to="/shop/bubble"
            >
              Shop $28
            </Link>
          </div>
          <div className={styles.fvis}>
            <BalmStick />
          </div>
        </div>
      </section>

      {/* ===== REVIEWS ===== */}
      <section className={styles.reviews}>
        <div className={`${styles.sec} ${styles.wrap}`} id="reviews">
          <div className={styles.secHead}>
            <div>
              <div className={styles.eyebrow}>Loved by 3,200+</div>
              <h2>What people are saying</h2>
            </div>
            <div style={{ color: 'var(--pink-deep)', fontWeight: 800 }}>★★★★★ 4.9 / 5</div>
          </div>
          <div className={styles.revGrid}>
            {REVIEWS.map((r, i) => (
              <div className={styles.rev} key={i}>
                <div className={styles.stars}>{STAR(r.s)}</div>
                <p>“{r.q}”</p>
                <div className={styles.who}>
                  <span className={styles.av} style={{ background: r.c }}>{r.n.charAt(0)}</span>
                  <div>
                    <b>{r.n}</b>
                    <span>{r.t}</span>
                  </div>
                  {r.verified && <span className={styles.verified}>✓ Verified</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TOP PRODUCTS TILES ===== */}
      <section className={`${styles.sec} ${styles.wrap}`} id="top-products">
        <div className={styles.secHead}>
          <div>
            <div className={styles.eyebrow}>Fan favorites</div>
            <h2>Top products</h2>
          </div>
          <Link to="/shop/bubble">Shop the bestseller →</Link>
        </div>
        <div className={styles.topGrid}>
          {PRODUCTS.map((p) => {
            const theme = TILE_THEME[p.slug] || TILE_THEME.bubble;
            return (
              <Link
                key={p.slug}
                to={`/shop/${p.slug}`}
                className={styles.tTile}
                style={{ background: theme.grad, color: theme.ink }}
                aria-label={`Shop ${p.cat} — ${p.name}`}
              >
                <div className={styles.tGlow} />
                <div className={styles.tCat}>{p.cat}</div>
                <div className={styles.tName}>{p.name}</div>
                <div className={styles.tVisual}>
                  {photos[p.slug]
                    ? <img src={photos[p.slug]} alt={p.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain', borderRadius: 12 }} />
                    : <ThumbVisual kind={p.visual} />}
                </div>
                <div className={styles.tFoot}>
                  <span className={styles.tPrice}>{p.price}</span>
                  <span className={styles.tShop}>Shop now <i>→</i></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ===== NEWSLETTER ===== */}
      <section className={styles.news}>
        <h2>Join the glow list</h2>
        <p>Early drops, restock alerts, and 10% off your first order.</p>
        <form onSubmit={subscribe}>
          <label htmlFor="news-email" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Email address
          </label>
          <input id="news-email" type="email" placeholder="your@email.com" required />
          <button type="submit">Sign up</button>
        </form>
      </section>

      {/* ===== FUTURE / INTERACTIVE 3D (pink) ===== */}
      <FutureHero />

      <Footer />
    </div>
  );
}
